type MetaPixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  push?: MetaPixel;
  queue?: unknown[][];
  version?: string;
};

declare global {
  interface Window {
    _fbq?: MetaPixel;
    fbq?: MetaPixel;
  }
}

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID?.trim();

function hasPixelId() {
  return Boolean(PIXEL_ID && !PIXEL_ID.includes("XXXXX") && !PIXEL_ID.startsWith("%VITE_"));
}

function loadPixelScript() {
  if (document.querySelector('script[data-traverba-meta-pixel="true"]')) return;

  const script = document.createElement("script");
  script.async = true;
  script.dataset.traverbaMetaPixel = "true";
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
}

function schedulePixelScript() {
  let scheduled = true;

  const load = () => {
    if (!scheduled) return;
    scheduled = false;
    loadPixelScript();
    ["pointerdown", "keydown", "touchstart", "scroll"].forEach((eventName) => {
      window.removeEventListener(eventName, load);
    });
  };

  ["pointerdown", "keydown", "touchstart", "scroll"].forEach((eventName) => {
    window.addEventListener(eventName, load, { once: true, passive: true });
  });

  if (document.readyState === "complete") load();
  else window.addEventListener("load", load, { once: true });
}

function getMetaPixel(): MetaPixel | undefined {
  if (!hasPixelId()) return undefined;
  const pixelId = PIXEL_ID as string;
  if (window.fbq) return window.fbq;

  const pixel: MetaPixel = (...args: unknown[]) => {
    if (pixel.callMethod) pixel.callMethod(...args);
    else pixel.queue?.push(args);
  };

  pixel.push = pixel;
  pixel.loaded = true;
  pixel.version = "2.0";
  pixel.queue = [];
  window.fbq = pixel;
  window._fbq = pixel;

  pixel("init", pixelId);
  schedulePixelScript();
  return pixel;
}

export function trackMetaPageView() {
  getMetaPixel()?.("track", "PageView");
}

export function trackMetaDownloadView() {
  getMetaPixel()?.("track", "ViewContent", {
    content_category: "app_download",
    content_name: "Traverba app download",
  });
}

export function trackMetaStoreClick(platform: "ios" | "android", location: string) {
  getMetaPixel()?.("trackCustom", "StoreClick", {
    platform,
    placement: location,
  });
}
